import { Component, computed, EventEmitter, inject, Input, OnDestroy, Output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import {
  MAX_RECORDING_TIME,
  MAX_RECORDING_SIZE_BYTES,
  PCM_BUFFER_SIZE,
  STREAMING_SILENCE_PAD_MS,
  TRANSCRIPTION_FLUSH_DELAY_MS,
} from './voice.constants';
import { SpeechToTextIconSvg } from './icons/speech-to-text-icon';
import { WaveformAnalyzer } from './waveform-analyzer';
import { PCM_PROCESSOR_CODE } from './workers/pcm-processor.worklet';

@Component({
  selector: 'voice-transcription-button',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule, TranslateModule, GalaxyLoadingSpinnerModule],
  templateUrl: './transcription-button.component.html',
  styleUrl: './transcription-button.component.scss',
})
export class TranscriptionButtonComponent implements OnDestroy {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackbarService = inject(SnackbarService);

  private stream: MediaStream | undefined;
  private recorder?: MediaRecorder;
  private maxDurationTimeout?: ReturnType<typeof setTimeout>;
  private currentRecordingSize = 0;
  private chunks: BlobPart[] = [];

  private recordingCancelled = false;
  private workletGeneration = 0;

  // AudioWorklet PCM capture (streaming mode)
  private audioContext?: AudioContext;
  private workletNode?: AudioWorkletNode;
  private sourceNode?: MediaStreamAudioSourceNode;

  @Input() useStreaming = false;
  @Input() isProcessing = false;
  @Input() disabled = false;

  @Output() transcriptionStarted = new EventEmitter<void>();
  @Output() batchAudioReady = new EventEmitter<Blob>();
  @Output() streamingAudioChunk = new EventEmitter<ArrayBuffer>();
  @Output() transcriptionStopped = new EventEmitter<void>();

  isRecording = signal(false);
  isFlushing = signal(false);

  // Batch-mode countdown shown next to the stop icon while recording.
  protected readonly remainingSeconds = signal(Math.floor(MAX_RECORDING_TIME / 1000));
  protected readonly remainingFormattedTime = computed(() => {
    const seconds = this.remainingSeconds();
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  });
  private batchCountdownInterval?: ReturnType<typeof setInterval>;

  // Bars + silence-autostop. Callback fires on SILENCE_AUTO_STOP_MS of silence.
  protected readonly waveform = new WaveformAnalyzer(() => this.stopTranscription());

  private flushTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    this.matIconRegistry.addSvgIconLiteral(
      'speech-to-text',
      this.sanitizer.bypassSecurityTrustHtml(SpeechToTextIconSvg),
    );
  }

  private isStopInProgress = false;

  toggleTranscription(): void {
    if (this.isRecording()) {
      this.stopTranscription();
    } else {
      this.startTranscription();
    }
  }

  private startTranscription(): void {
    this.isStopInProgress = false;
    this.clearFlushTimeout();
    this.isFlushing.set(false);
    if (this.useStreaming) {
      this.startStreamingTranscription();
    } else {
      this.startBatchTranscription();
    }
  }

  private stopTranscription(): void {
    // Guard against double-stop from competing timers
    if (this.isStopInProgress || !this.isRecording()) {
      return;
    }
    this.isStopInProgress = true;

    if (this.useStreaming) {
      this.stopStreamingTranscription();
    } else {
      this.stopBatchTranscription();
    }
  }

  private startBatchTranscription(): void {
    this.startBatchCountdown();
    this.startMediaCapture(
      () => this.transcriptionStarted.emit(),
      () => this.stopTranscription(),
    );
  }

  private stopBatchTranscription(): void {
    this.clearMaxDurationTimer();
    this.clearBatchCountdown();
    this.currentRecordingSize = 0;
    if (!this.recorder || this.recorder.state === 'inactive') {
      this.isRecording.set(false);
      return;
    }

    this.recorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: 'audio/webm' });
      this.chunks = [];
      this.batchAudioReady.emit(blob);
      this.isRecording.set(false);
      this.recorder = undefined;
    };

    this.recorder.stop();
    this.stopMediaTracks();
  }

  private startStreamingTranscription(): void {
    if (this.isRecording() || this.isFlushing()) {
      return;
    }
    this.transcriptionStarted.emit();
  }

  async beginStreamingRecording(): Promise<void> {
    this.recordingCancelled = false;
    this.startMaxDurationTimer();

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: { ideal: 48000 },
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      if (this.recordingCancelled) {
        this.stopMediaTracks();
        return;
      }

      this.audioContext = new AudioContext({ sampleRate: 48000 });

      const blob = new Blob([PCM_PROCESSOR_CODE], { type: 'application/javascript' });
      const processorUrl = URL.createObjectURL(blob);
      await this.audioContext.audioWorklet.addModule(processorUrl);
      URL.revokeObjectURL(processorUrl);

      if (this.recordingCancelled) {
        this.stopMediaTracks();
        this.teardownAudioWorklet();
        return;
      }

      this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');

      // Closure-captured generation: rejects stale messages from a previous worklet
      // (e.g. flush response after a new session began) without dropping the current flush.
      const myGeneration = ++this.workletGeneration;
      this.workletNode.port.onmessage = (event) => {
        if (myGeneration !== this.workletGeneration) {
          return;
        }
        if (event.data.type === 'audio') {
          this.streamingAudioChunk.emit(event.data.samples);
        }
      };

      this.sourceNode.connect(this.workletNode);
      this.workletNode.connect(this.audioContext.destination);

      this.isRecording.set(true);
      this.waveform.setup(this.audioContext, this.sourceNode);
    } catch (err) {
      console.error('Could not start PCM streaming transcription:', err);
      this.snackbarService.openErrorSnack('INBOX.UPLOAD_FILE.VOICE_RECORDING.MIC_ERROR');
      this.isRecording.set(false);
      this.isStopInProgress = false;
      this.clearMaxDurationTimer();
      this.teardownAudioWorklet();
    }
  }

  cancelStreamingRecording(): void {
    this.recordingCancelled = true;
    this.clearFlushTimeout();
    this.isFlushing.set(false);
    this.isStopInProgress = false;
    this.isRecording.set(false);
    this.clearMaxDurationTimer();
    this.teardownAudioWorklet();
  }

  private stopStreamingTranscription(): void {
    this.clearMaxDurationTimer();

    // Worklet's flush response still reaches the parent via the generation guard.
    this.teardownAudioWorklet();
    this.stopMediaTracks();
    this.isRecording.set(false);
    this.isFlushing.set(true);

    // Backend Deepgram VAD needs silence FRAMES (not just absence of audio) to
    // fire SpeechFinal/UtteranceEnd and commit the utterance.
    this.emitSilencePadding();

    this.clearFlushTimeout();
    this.flushTimeout = setTimeout(() => {
      this.flushTimeout = undefined;
      this.completeFlush();
    }, TRANSCRIPTION_FLUSH_DELAY_MS);
  }

  private emitSilencePadding(): void {
    // Same chunk size as the worklet so the backend sees consistent pacing.
    const SAMPLES_PER_CHUNK = PCM_BUFFER_SIZE;
    const SAMPLE_RATE = 48_000;
    const totalSamples = Math.floor((STREAMING_SILENCE_PAD_MS / 1000) * SAMPLE_RATE);
    const paddingGeneration = this.workletGeneration;
    let emitted = 0;

    const emitNext = (): void => {
      if (paddingGeneration !== this.workletGeneration || !this.isFlushing()) {
        return;
      }
      if (emitted >= totalSamples) {
        return;
      }
      const remaining = totalSamples - emitted;
      const chunkSize = Math.min(SAMPLES_PER_CHUNK, remaining);
      const silent = new Int16Array(chunkSize);
      this.streamingAudioChunk.emit(silent.buffer);
      emitted += chunkSize;
      setTimeout(emitNext, 0);
    };
    emitNext();
  }

  /** Close the flushing window early. Idempotent. */
  completeFlush(): void {
    if (!this.isFlushing()) {
      return;
    }
    this.clearFlushTimeout();
    this.isFlushing.set(false);
    this.isStopInProgress = false;
    this.transcriptionStopped.emit();
  }

  private clearFlushTimeout(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = undefined;
    }
  }

  private teardownAudioWorklet(): void {
    this.waveform.teardown();
    if (this.workletNode) {
      // Flush partial buffer (critical for very short recordings where audio
      // fits entirely in the partial buffer). onmessage stays
      // bound; the workletGeneration closure guards against stale chunks.
      this.workletNode.port.postMessage({ type: 'flush' });
      this.workletNode.disconnect();
      this.workletNode = undefined;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = undefined;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      // Delay close so the flush response can round-trip through the message channel.
      const ctx = this.audioContext;
      this.audioContext = undefined;
      setTimeout(() => ctx.close().catch(() => undefined), 50);
    }
  }

  private startMediaCapture(onStarted?: () => void, onTimeout?: () => void): void {
    this.startMaxDurationTimer();
    this.currentRecordingSize = 0;
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream;
        this.recorder = new MediaRecorder(stream);
        this.recorder.ondataavailable = (ev) => {
          this.chunks.push(ev.data);
          this.currentRecordingSize += ev.data.size;
          if (this.currentRecordingSize >= MAX_RECORDING_SIZE_BYTES) {
            onTimeout?.();
          }
        };
        this.recorder.start(1000);
        this.isRecording.set(true);
        onStarted?.();
      })
      .catch((err) => {
        console.error('Could not start recording:', err);
        this.snackbarService.openErrorSnack('INBOX.UPLOAD_FILE.VOICE_RECORDING.MIC_ERROR');
        this.isRecording.set(false);
        this.clearMaxDurationTimer();
        this.clearBatchCountdown();
      });
  }

  private stopMediaTracks(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = undefined;
    }
  }

  private startMaxDurationTimer(): void {
    this.clearMaxDurationTimer();
    this.maxDurationTimeout = setTimeout(() => this.stopTranscription(), MAX_RECORDING_TIME);
  }

  private clearMaxDurationTimer(): void {
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = undefined;
    }
  }

  private startBatchCountdown(): void {
    this.clearBatchCountdown();
    this.remainingSeconds.set(Math.floor(MAX_RECORDING_TIME / 1000));
    this.batchCountdownInterval = setInterval(() => {
      this.remainingSeconds.update((v) => Math.max(0, v - 1));
    }, 1000);
  }

  private clearBatchCountdown(): void {
    if (this.batchCountdownInterval) {
      clearInterval(this.batchCountdownInterval);
      this.batchCountdownInterval = undefined;
    }
    this.remainingSeconds.set(Math.floor(MAX_RECORDING_TIME / 1000));
  }

  ngOnDestroy(): void {
    // Emit transcriptionStopped synchronously if a flush was pending — parents
    // tracking "recording active" state need to clear it before the component
    // is gone, otherwise their state stays stuck after a re-create.
    const hadPendingFlush = this.flushTimeout !== undefined || this.isFlushing();
    this.clearMaxDurationTimer();
    this.clearBatchCountdown();
    this.clearFlushTimeout();
    this.isFlushing.set(false);
    this.isStopInProgress = false;
    this.teardownAudioWorklet();
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.stop();
    }
    this.stopMediaTracks();
    if (hadPendingFlush) {
      this.transcriptionStopped.emit();
    }
  }
}
